import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { billsApi, servicesApi } from '../../services/smartBillApi';
import axios from 'axios';
import './PostBillCoupons.css';

const getApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (envBase) {
    // Remove trailing slash and /api if present
    let base = envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
    if (base.endsWith('/api')) {
      base = base.slice(0, -4);
    }
    return base;
  }
  return 'http://localhost:3000';
};

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const PostBillCoupons = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone') || '';
  const customerName = searchParams.get('name') || '';

  const [customerCoupons, setCustomerCoupons] = useState([]);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigningCoupon, setAssigningCoupon] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    used: 0,
    expired: 0,
  });

  useEffect(() => {
    if (phone) {
      fetchCustomerCoupons();
      fetchAvailableCoupons();
    }
  }, [phone]);

  // Automatically open next tabs when page loads (customer history - coupons page)
  useEffect(() => {
    // Only auto-open tabs if we have a phone number (customer is loaded)
    if (phone && phone.length >= 10) {
      // Delay to ensure page is fully loaded
      const timer = setTimeout(() => {
        // Tab 1: View Bills - Open automatically
        const viewBillsUrl = `${window.location.origin}/bills/view`;
        const viewBillsWindow = window.open(viewBillsUrl, '_blank');
        // Focus the View Bills tab
        if (viewBillsWindow) {
          viewBillsWindow.focus();
        }
        
        // Tab 2: Coupons Issued page - Open automatically
        const couponsIssuedUrl = `${window.location.origin}/coupons?tab=issued`;
        window.open(couponsIssuedUrl, '_blank');
      }, 1000); // Small delay to ensure page is ready

      return () => clearTimeout(timer);
    }
  }, [phone]);

  const fetchCustomerCoupons = async () => {
    if (!phone) return;

    try {
      setLoading(true);
      const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
      
      // Get customer coupons from smartbills API
      try {
        const baseUrl = getApiBaseUrl();
        const couponsResponse = await axios.get(
          `${baseUrl}/api/smartbills/customers/${cleanPhone}/coupons`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        if (couponsResponse.data.success) {
          setCustomerCoupons(couponsResponse.data.coupons || []);
          setStatistics(couponsResponse.data.summary || {
            total: 0,
            active: 0,
            used: 0,
            expired: 0,
          });
        }
      } catch (err) {
        console.error('Error fetching coupons:', err);
        setCustomerCoupons([]);
      }
    } catch (err) {
      console.error('Failed to load customer coupons:', err);
      toast.error('Failed to load customer coupons');
      setCustomerCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCoupons = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/coupons`, {
        params: { status: 'active', limit: 1000 },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.success) {
        setAvailableCoupons(response.data.coupons || []);
      }
    } catch (err) {
      console.error('Failed to load available coupons:', err);
    }
  };

  const handleAssignCoupon = async () => {
    if (!selectedCouponId) {
      toast.error('Please select a coupon to assign');
      return;
    }

    try {
      setAssigningCoupon(true);
      const baseUrl = getApiBaseUrl();
      const response = await axios.post(
        `${baseUrl}/api/coupons/assign`,
        {
          coupon_id: selectedCouponId,
          customer_phone: phone.replace(/\D/g, '').slice(0, 10),
          customer_name: customerName,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Coupon assigned successfully!');
        setShowAssignModal(false);
        setSelectedCouponId('');
        await fetchCustomerCoupons();
      } else {
        throw new Error(response.data.message || 'Failed to assign coupon');
      }
    } catch (err) {
      console.error('Failed to assign coupon:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to assign coupon');
    } finally {
      setAssigningCoupon(false);
    }
  };

  const handleClaimCoupon = async (coupon) => {
    if (coupon.status !== 'active') {
      toast.error('This coupon is not active');
      return;
    }

    // Get service details from coupon
    let serviceDetails = null;
    if (coupon.service_id || coupon.service_name) {
      try {
        const servicesResponse = await servicesApi.getAll('', '', false);
        if (servicesResponse.success && servicesResponse.data) {
          serviceDetails = servicesResponse.data.find(
            (s) => s.id === coupon.service_id || s.name === coupon.service_name
          );
        }
      } catch (err) {
        console.error('Error fetching service details:', err);
      }
    }

    // Prepare bill data with coupon details
    const billData = {
      customerData: {
        name: customerName || coupon.customer_name || '',
        phone: phone.replace(/\D/g, '').slice(0, 10),
        profession: '',
        address: '',
        remarks: `Coupon: ${coupon.name || coupon.coupon_name || 'N/A'}`,
      },
      selectedServices: serviceDetails
        ? [
            {
              id: serviceDetails.id,
              name: serviceDetails.name,
              displayName: serviceDetails.name,
              mainCategory: serviceDetails.main_category || '',
              subCategory: serviceDetails.sub_category || '',
              price: Number(coupon.actual_price || serviceDetails.price || 0),
              serviceCharge: Number(coupon.offer_price || coupon.final_price || (coupon.actual_price || serviceDetails.price || 0)),
              challanaAmount: Number(serviceDetails.challana || 0),
              quantity: 1,
              total: Number(coupon.offer_price || coupon.final_price || (coupon.actual_price || serviceDetails.price || 0)) + Number(serviceDetails.challana || 0),
            },
          ]
        : [],
      couponData: {
        couponId: coupon.id, // Store coupon ID to mark as used
        couponName: coupon.name || coupon.coupon_name,
        originalPrice: coupon.actual_price || 0,
        offerPrice: coupon.offer_price || coupon.final_price || 0,
        discountAmount: (coupon.actual_price || 0) - (coupon.offer_price || coupon.final_price || 0),
      },
    };

    // Store in sessionStorage for SmartBillWithQR to pick up
    sessionStorage.setItem('couponBillData', JSON.stringify(billData));
    
    toast.success('Redirecting to bill form with coupon details...');
    navigate('/bills/create');
  };

  const getCouponAmount = (coupon) => {
    if (coupon.offer_price) return Number(coupon.offer_price);
    if (coupon.final_price) return Number(coupon.final_price);
    if (coupon.actual_price) return Number(coupon.actual_price);
    return 0;
  };

  const getOriginalAmount = (coupon) => {
    return Number(coupon.actual_price || coupon.original_price || 0);
  };

  const getDiscountAmount = (coupon) => {
    const original = getOriginalAmount(coupon);
    const offer = getCouponAmount(coupon);
    return Math.max(original - offer, 0);
  };

  // Include active, used, and expired coupons for display
  const activeCoupons = customerCoupons.filter((c) => {
    const status = c.status || 'active';
    if (status === 'used' || status === 'active') return true;
    // Also include expired coupons
    const validUntil = c.valid_until || c.validUntil;
    if (validUntil && new Date(validUntil) < new Date()) return true;
    return status === 'expired';
  });

  return (
    <div className="post-bill-coupons-page">
      <header className="post-bill-coupons-header">
        <div className="header-content">
          <div>
            <h1>Customer Coupons Management</h1>
            <p>
              Manage coupons for: <strong>{customerName || 'Customer'}</strong> ({phone})
            </p>
          </div>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowAssignModal(true)}>
              <i className="fas fa-plus-circle"></i> Assign Coupon
            </button>
            <button className="btn btn-secondary" onClick={fetchCustomerCoupons} disabled={loading}>
              <i className="fas fa-sync"></i> Refresh
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/bills/create')}>
              <i className="fas fa-arrow-left"></i> Back to Bills
            </button>
          </div>
        </div>
      </header>

      <div className="coupons-statistics">
        <div className="stat-card">
          <span className="stat-label">Total Coupons</span>
          <span className="stat-value">{statistics.total}</span>
        </div>
        <div className="stat-card active">
          <span className="stat-label">Active</span>
          <span className="stat-value">{statistics.active}</span>
        </div>
        <div className="stat-card used">
          <span className="stat-label">Used</span>
          <span className="stat-value">{statistics.used}</span>
        </div>
        <div className="stat-card expired">
          <span className="stat-label">Expired</span>
          <span className="stat-value">{statistics.expired}</span>
        </div>
      </div>

      {loading && customerCoupons.length === 0 ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading coupons...</p>
        </div>
      ) : activeCoupons.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-ticket-alt"></i>
          <h3>No Active Coupons</h3>
          <p>This customer doesn't have any active coupons. Assign a coupon to get started.</p>
          <button className="btn btn-primary" onClick={() => setShowAssignModal(true)}>
            <i className="fas fa-plus-circle"></i> Assign Coupon
          </button>
        </div>
      ) : (
        <div className="coupons-grid">
          {activeCoupons.map((coupon) => {
            const originalAmount = getOriginalAmount(coupon);
            const offerAmount = getCouponAmount(coupon);
            const discountAmount = getDiscountAmount(coupon);
            const isUsed = coupon.status === 'used';
            // Check if coupon is expired
            const validUntil = coupon.valid_until || coupon.validUntil;
            const isExpired = validUntil ? new Date(validUntil) < new Date() : false;

            return (
              <div 
                key={coupon.id} 
                className={`coupon-card ${isUsed ? 'used-coupon' : ''} ${isExpired ? 'expired-coupon' : ''}`} 
                style={
                  isUsed 
                    ? { opacity: 0.6, backgroundColor: '#f5f5f5', border: '1px solid #ddd' }
                    : isExpired
                    ? { opacity: 0.7, backgroundColor: '#fef2f2', border: '1px solid #fca5a5' }
                    : {}
                }
              >
                <div className="coupon-header" style={isExpired ? { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' } : {}}>
                  <h3 style={isUsed || isExpired ? { color: '#fff' } : {}}>{coupon.name || coupon.coupon_name || 'Coupon'}</h3>
                  <span className={`coupon-status ${isUsed ? 'used' : isExpired ? 'expired' : 'active'}`}>
                    {isUsed ? 'Used' : isExpired ? 'Expired' : 'Active'}
                  </span>
                </div>
                <div className="coupon-body">
                  {coupon.service_name && (
                    <div className="coupon-service">
                      <i className="fas fa-tag"></i> {coupon.service_name}
                    </div>
                  )}
                  <div className="coupon-pricing">
                    {originalAmount > 0 && (
                      <div className="price-row">
                        <span className="price-label">Original Price:</span>
                        <span className="price-value original">{formatCurrency(originalAmount)}</span>
                      </div>
                    )}
                    <div className="price-row">
                      <span className="price-label">Offer Price:</span>
                      <span className="price-value offer">{formatCurrency(offerAmount)}</span>
                    </div>
                    {discountAmount > 0 && (
                      <div className="price-row">
                        <span className="price-label">You Save:</span>
                        <span className="price-value savings">{formatCurrency(discountAmount)}</span>
                      </div>
                    )}
                  </div>
                  {coupon.valid_until && (
                    <div className="coupon-validity">
                      <i className="fas fa-calendar"></i> Valid until:{' '}
                      {new Date(coupon.valid_until).toLocaleDateString('en-IN')}
                    </div>
                  )}
                </div>
                <div className="coupon-actions">
                  <button 
                    className="btn btn-success btn-claim" 
                    onClick={() => handleClaimCoupon(coupon)}
                    disabled={isUsed || isExpired}
                    style={isUsed || isExpired ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#ccc' } : {}}
                  >
                    <i className="fas fa-check-circle"></i> {isUsed ? 'Already Used' : isExpired ? 'Expired' : 'Claim & Create Bill'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {customerCoupons.filter((c) => c.status !== 'active').length > 0 && (
        <div className="used-coupons-section">
          <h3>Used/Expired Coupons</h3>
          <div className="coupons-list">
            {customerCoupons
              .filter((c) => c.status !== 'active')
              .map((coupon) => (
                <div key={coupon.id} className="coupon-item used">
                  <div className="coupon-item-info">
                    <span className="coupon-name">{coupon.name || coupon.coupon_name}</span>
                    <span className="coupon-status-badge">{coupon.status}</span>
                  </div>
                  {coupon.used_at && (
                    <div className="coupon-item-date">
                      Used on: {new Date(coupon.used_at).toLocaleDateString('en-IN')}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="assign-modal">
          <div className="assign-modal-backdrop" onClick={() => setShowAssignModal(false)}></div>
          <div className="assign-modal-content">
            <h3>Assign Coupon to Customer</h3>
            <div className="assign-form">
              <div className="form-group">
                <label>Select Coupon *</label>
                <select
                  value={selectedCouponId}
                  onChange={(e) => setSelectedCouponId(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Select a coupon --</option>
                  {availableCoupons.map((coupon) => {
                    // Check if already assigned and not used
                    const alreadyAssigned = customerCoupons.some(
                      (c) =>
                        (c.coupon_id === coupon.id || c.coupon_id === coupon.coupon_id) &&
                        c.status === 'active'
                    );
                    return (
                      <option
                        key={coupon.id || coupon.coupon_id}
                        value={coupon.id || coupon.coupon_id}
                        disabled={alreadyAssigned}
                      >
                        {coupon.name || coupon.coupon_name} - {coupon.service_name || 'General'}
                        {alreadyAssigned ? ' (Already Assigned)' : ''}
                      </option>
                    );
                  })}
                </select>
                {selectedCouponId && (
                  <small className="form-hint">
                    This coupon will be assigned to {customerName || 'customer'} ({phone})
                  </small>
                )}
              </div>
            </div>
            <div className="assign-modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAssignCoupon}
                disabled={!selectedCouponId || assigningCoupon}
              >
                {assigningCoupon ? 'Assigning...' : 'Assign Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostBillCoupons;

